import { createElement } from 'react';
import { z } from 'zod';

import {
    type DynamicWidgetAdapter,
    type DynamicWidgetBuildContext,
} from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_registry';
import {
    buildBindingPresentation,
    buildDynamicWidgetId,
    type ClientDetailWidgetModel,
    clientDetailWidgetModelSchema,
    type ClientDetailWidgetRecord,
    type DynamicWidgetIssue,
    getDynamicWidgetSourceId,
} from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_schema';
import {
    addRetrieveEnvelopeBindings,
    buildRetrieveArtifact,
    matchesRetrieveDomain,
} from '@apps/web/components/phoebe_chat_view/dynamic_widgets/retrieve_artifact_adapter';
import { RetrieveArtifactController } from '@apps/web/components/phoebe_chat_view/dynamic_widgets/retrieve_artifact_controller';
import { ClientDetailWidget } from '@apps/web/components/phoebe_chat_view/dynamic_widgets/retrieve_client_detail_widget';
import { type ToolExecutionEntry } from '@apps/web/components/phoebe_chat_view/transcript_types';

const DOMAIN = 'clients';

const clientSourceRowSchema = z.object({
    client_id: z.string().min(1),
    display_name: z.string().min(1),
    preferred_name: z.string().nullable(),
    office: z.string().nullable(),
    city: z.string().nullable(),
    state: z.string().nullable(),
    tags: z.array(z.string()),
    active: z.boolean(),
});

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function optionalString(value: unknown): string | null {
    return typeof value === 'string' && value.length > 0 ? value : null;
}

function mapClientRecord(rawRow: unknown): ClientDetailWidgetRecord {
    const parsed = clientSourceRowSchema.safeParse(rawRow);
    if (parsed.success) {
        return {
            clientId: parsed.data.client_id,
            displayName: parsed.data.display_name,
            preferredName: parsed.data.preferred_name,
            office: parsed.data.office,
            city: parsed.data.city,
            state: parsed.data.state,
            tags: parsed.data.tags,
            active: parsed.data.active,
            valid: true,
        };
    }
    const row = isRecord(rawRow) ? rawRow : {};
    return {
        clientId: optionalString(row['client_id']),
        displayName: optionalString(row['display_name']),
        preferredName: optionalString(row['preferred_name']),
        office: optionalString(row['office']),
        city: optionalString(row['city']),
        state: optionalString(row['state']),
        tags:
            Array.isArray(row['tags']) &&
            row['tags'].every(
                (item): item is string => typeof item === 'string',
            )
                ? row['tags']
                : [],
        active: typeof row['active'] === 'boolean' ? row['active'] : null,
        valid: false,
    };
}

export function hydrateRetrieveClientDetailWidgetModel(
    model: ClientDetailWidgetModel,
    rawRows: ReadonlyArray<unknown>,
    provenance: 'event_preview' | 'resource',
): ClientDetailWidgetModel {
    const sourceKind =
        provenance === 'event_preview' ? 'event_source' : 'resource_source';
    const source = model.sources.find(
        (candidate) => candidate.kind === sourceKind,
    );
    const sourceId =
        source === undefined ? null : getDynamicWidgetSourceId(source);
    const record =
        rawRows[0] === undefined ? null : mapClientRecord(rawRows[0]);
    const rowIssue: DynamicWidgetIssue | null =
        record !== null && record.valid
            ? null
            : {
                  id: `${model.widgetId}.client-record`,
                  code: 'source_invalid',
                  level: 'warning',
                  scope: 'section',
                  bindingIds: [
                      provenance === 'event_preview'
                          ? 'previewRows'
                          : 'resourceRows',
                  ],
                  sourceIds: sourceId === null ? [] : [sourceId],
                  retryable: false,
                  safeDetails: { rowIndex: 0 },
              };
    return clientDetailWidgetModelSchema.parse({
        ...model,
        record,
        rowProvenance: provenance,
        issues: [
            ...model.issues.filter(
                (issue) => issue.id !== `${model.widgetId}.client-record`,
            ),
            ...(rowIssue === null ? [] : [rowIssue]),
        ],
    });
}

export function buildRetrieveClientDetailWidgetModel(
    entry: ToolExecutionEntry,
    context: DynamicWidgetBuildContext,
): ClientDetailWidgetModel | null {
    const artifact = buildRetrieveArtifact(entry, context, DOMAIN);
    if (
        artifact === null ||
        artifact.envelope.row_count !== 1 ||
        !entry.toolCallEventId ||
        !entry.runId
    ) {
        return null;
    }
    addRetrieveEnvelopeBindings({ artifact });
    artifact.builder.addCalculatedField({
        id: 'preview.record.fields',
        sourceIds: [artifact.eventSourceId],
        paths: [
            '/head_preview/0/client_id',
            '/head_preview/0/display_name',
            '/head_preview/0/preferred_name',
            '/head_preview/0/office',
            '/head_preview/0/city',
            '/head_preview/0/state',
            '/head_preview/0/tags',
            '/head_preview/0/active',
        ],
        transform: 'mapRetrieveClientPreviewRecord',
        rawValue: artifact.envelope.head_preview[0] ?? null,
        value: null,
        renderedValue: null,
        presentation: buildBindingPresentation({
            section: 'record',
            label: 'fields',
        }),
    });
    artifact.builder.addLiveField({
        id: 'resource.record.fields',
        sourceIds: [artifact.resourceSourceId],
        paths: [
            '/rows/0/client_id',
            '/rows/0/display_name',
            '/rows/0/preferred_name',
            '/rows/0/office',
            '/rows/0/city',
            '/rows/0/state',
            '/rows/0/tags',
            '/rows/0/active',
        ],
        presentation: buildBindingPresentation({
            section: 'record',
            label: 'fields',
        }),
    });
    const receiptModel = clientDetailWidgetModelSchema.parse({
        ...artifact.builder.buildBase({
            widgetId: buildDynamicWidgetId(entry.toolCallEventId, 'detail'),
            templateVersion: 1,
            issues: [],
        }),
        template: 'detail',
        templateVersion: 1,
        record: null,
        rowCount: 1,
        rowProvenance: 'event_preview',
        workspaceFile: {
            runId: entry.runId,
            path: artifact.workspacePath,
        },
    });
    if (artifact.envelope.head_preview.length === 0) {
        return receiptModel;
    }
    return hydrateRetrieveClientDetailWidgetModel(
        receiptModel,
        artifact.envelope.head_preview,
        'event_preview',
    );
}

export const retrieveClientDetailAdapter: DynamicWidgetAdapter<ClientDetailWidgetModel> =
    {
        template: 'detail',
        templateVersion: 1,
        toolNames: new Set(['retrieve', 'retrieve_clients']),
        matches: (entry) => matchesRetrieveDomain(entry, DOMAIN),
        build: buildRetrieveClientDetailWidgetModel,
        render(model, handlers) {
            return createElement(
                RetrieveArtifactController<ClientDetailWidgetModel>,
                {
                    model,
                    hydrateRows: (previewModel, rows) =>
                        hydrateRetrieveClientDetailWidgetModel(
                            previewModel,
                            rows,
                            'resource',
                        ),
                    render: ({ model: completeModel, loadError }) =>
                        createElement(ClientDetailWidget, {
                            model: completeModel,
                            loadError,
                            onClientClick: handlers.onClientClick,
                        }),
                },
            );
        },
    };
